import React, { useEffect, useState } from "react";
import "./Knowledge.css";
import {
  KnowledgeEntity,
  KnowledgeLinkRef,
  KnowledgeRelatedEntity,
  getKnowledgeEntity,
  getKnowledgeEntityLinks,
  getRelatedKnowledgeEntities,
} from "../utils/api";
import { entityExternalUrl, kindPresentation, mentionsLabel } from "../utils/knowledge";

interface KnowledgeEntityViewProps {
  entityId: string;
  onBack: () => void;
  onSelectEntity: (id: string) => void;
  onSelectLink: (id: string) => void;
  initialData?: {
    entity: KnowledgeEntity;
    related: KnowledgeRelatedEntity[];
    links: KnowledgeLinkRef[];
  };
  previewMode?: boolean;
}

/**
 * Properties the header already covers, plus the external ids behind the "open
 * in…" button. Those identify the entity to Google and Spotify, not to a person
 * reading the screen.
 */
const COVERED_PROPS = new Set([
  "google_maps_pin",
  "spotify_url",
  "url",
  "title",
  "google_place_id",
  "spotify_track_id",
]);

const PROP_LABELS: Record<string, string> = {
  address: "Address",
  category: "Category",
  artist: "Artist",
  album: "Album",
  servings: "Servings",
  prep_time: "Prep time",
  cook_time: "Cook time",
  domain: "Source",
};

const KnowledgeEntityView: React.FC<KnowledgeEntityViewProps> = ({
  entityId,
  onBack,
  onSelectEntity,
  onSelectLink,
  initialData,
  previewMode = false,
}) => {
  const preview = previewMode && Boolean(initialData);
  const [entity, setEntity] = useState<KnowledgeEntity | null>(initialData?.entity ?? null);
  const [related, setRelated] = useState<KnowledgeRelatedEntity[]>(initialData?.related ?? []);
  const [links, setLinks] = useState<KnowledgeLinkRef[]>(initialData?.links ?? []);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preview) return;

    let mounted = true;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        // The entity first: the other two are meaningless if it does not exist,
        // and this is what turns a name in the URL into a resolved entity.
        const resolved = await getKnowledgeEntity(entityId);
        if (!mounted) return;
        setEntity(resolved);

        const [nextRelated, nextLinks] = await Promise.all([
          getRelatedKnowledgeEntities(resolved.id, { limit: 24 }),
          getKnowledgeEntityLinks(resolved.id, 25),
        ]);
        if (!mounted) return;
        setRelated(nextRelated);
        setLinks(nextLinks);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [entityId, preview]);

  if (loading) {
    return (
      <section className="app-section">
        <div className="app-card knowledge-status">Loading…</div>
      </section>
    );
  }

  if (error || !entity) {
    return (
      <section className="app-section">
        <div className="app-card knowledge-error">
          <p>
            <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {error ?? "Not found."}
          </p>
          <button type="button" className="back-button" onClick={onBack}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> Back
          </button>
        </div>
      </section>
    );
  }

  const presentation = kindPresentation(entity.kind);
  const external = entityExternalUrl(entity);
  const properties = Object.entries(entity.props).filter(
    ([key, value]) => !COVERED_PROPS.has(key) && value !== null && value !== undefined && value !== "",
  );

  return (
    <>
      <section className="app-section">
        <div className="app-card">
          <button type="button" className="back-button" onClick={onBack}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> Knowledge
          </button>

          <div className="knowledge-entity-heading">
            <span className={`knowledge-row-icon kind--${entity.kind}`}>
              <i className={`fas ${presentation.icon}`} aria-hidden="true" />
            </span>
            <div className="knowledge-entity-identity">
              <h2 className="knowledge-entity-name">{entity.label}</h2>
              {/* The detail line is not repeated here: whatever identifies this
                  entity is listed in full under Properties just below. */}
              <p className="knowledge-entity-meta">
                {presentation.label} · seen in {mentionsLabel(entity.mentions)}
              </p>
            </div>
          </div>

          {external && (
            <a
              className="app-button primary-button knowledge-external"
              href={external.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" /> {external.label}
            </a>
          )}

          {properties.length > 0 && (
            <dl className="knowledge-props">
              {properties.map(([key, value]) => (
                <React.Fragment key={key}>
                  <dt>{PROP_LABELS[key] ?? key.replace(/_/g, " ")}</dt>
                  <dd>{String(value)}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>
      </section>

      {related.length > 0 && (
        <section className="app-section">
          <div className="app-card">
            <h3 className="app-title knowledge-section-title">Appears with</h3>
            <p className="knowledge-section-hint">
              Things that turned up in the same links, and what they in turn led to.
            </p>
            <div className="knowledge-chips">
              {related.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="knowledge-chip"
                  onClick={() => onSelectEntity(item.id)}
                  title={`${kindPresentation(item.kind).label} · ${mentionsLabel(item.mentions)}`}
                >
                  <i className={`fas ${kindPresentation(item.kind).icon}`} aria-hidden="true" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="app-section">
        <div className="app-card">
          <h3 className="app-title knowledge-section-title">Where this came from</h3>
          {links.length === 0 ? (
            <p className="knowledge-status">No saved link reaches this any more.</p>
          ) : (
            <ul className="knowledge-list">
              {links.map((link) => (
                <li key={link.id}>
                  <button
                    type="button"
                    className="knowledge-row"
                    onClick={() => onSelectLink(link.id)}
                  >
                    <span className="knowledge-row-icon kind--link">
                      <i className="fas fa-link" aria-hidden="true" />
                    </span>
                    <span className="knowledge-row-text">
                      <span className="knowledge-row-label">{link.title || link.url}</span>
                      <span className="knowledge-row-detail">{link.domain}</span>
                    </span>
                    {/* Two hops means the link named something that named this —
                        a reel that credited the artist by naming their song. */}
                    {link.depth > 1 && <span className="knowledge-row-mentions">indirect</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
};

export default KnowledgeEntityView;
